import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Button from './ui/Button';
import Icon from './AppIcon';
import TouchpointImageUploader from './touchpoint-media/TouchpointImageUploader';

const DEFAULT_INTRO_TEMPLATE =
  `Hey {first_name}, it's {agent_name}, I added a dedicated phone number for client communication, ` +
  `please make sure to save this number so you know it's me when I reach out! ` +
  `Just reply stop if you'd rather not get messages from me.`;

const PERSONALITY_TYPES = [
  { id: 'professional', name: 'Professional & Polished', description: 'Formal but friendly. Direct and clear.', icon: 'Briefcase' },
  { id: 'warm_personal', name: 'Warm & Personal', description: 'Caring and friendly. Shows genuine warmth.', icon: 'Heart' },
  { id: 'question_based', name: 'Question-Based', description: 'Engages with questions. Conversational.', icon: 'MessageCircle' },
  { id: 'observational', name: 'Observational', description: 'Comments on seasons and surroundings.', icon: 'Eye' },
];

const PersonalProfileSettings = () => {
  const { userProfile, reloadProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [personalityTypes, setPersonalityTypes] = useState(['professional', 'warm_personal', 'question_based', 'observational']);
  const [communicationStyle, setCommunicationStyle] = useState('warm');
  const [introTemplate, setIntroTemplate] = useState('');
  const [introIsCustom, setIntroIsCustom] = useState(false);
  const [firstContactImageUrl, setFirstContactImageUrl] = useState(null);

  useEffect(() => {
    if (userProfile) {
      if (userProfile.message_personality_types && Array.isArray(userProfile.message_personality_types)) {
        setPersonalityTypes(userProfile.message_personality_types);
      } else if (userProfile.message_personality_type) {
        setPersonalityTypes([userProfile.message_personality_type]);
      }
      setCommunicationStyle(userProfile.communication_style || 'warm');
      if (userProfile.first_contact_intro_template) {
        setIntroTemplate(userProfile.first_contact_intro_template);
        setIntroIsCustom(true);
      } else {
        setIntroTemplate(DEFAULT_INTRO_TEMPLATE);
        setIntroIsCustom(false);
      }
      setFirstContactImageUrl(userProfile.first_contact_image_url || null);
    }
  }, [userProfile]);

  const togglePersonalityType = (typeId) => {
    setPersonalityTypes((prev) => {
      if (prev.includes(typeId)) {
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== typeId);
      }
      return [...prev, typeId];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated. Please log in again.');

      const response = await fetch('/api/user/profile-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          messagePersonalityTypes: personalityTypes,
          communicationStyle,
          firstContactIntroTemplate: introIsCustom ? introTemplate : null,
          firstContactImageUrl,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to save profile');
      if (reloadProfile) await reloadProfile();
      setMessage('Profile settings saved — upcoming touchpoints were refreshed.');
    } catch (error) {
      setMessage(`Failed to save settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Message Personalization</h2>
        <p className="text-muted-foreground">Customize how your touchpoint messages sound</p>
      </div>
      {message && (
        <div className={`p-4 rounded-lg mb-6 ${message.includes('Failed') ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'}`}>
          {message}
        </div>
      )}
      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Message Styles</h3>
          <p className="text-sm text-muted-foreground mb-3">Select one or more styles — we rotate through them each month</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PERSONALITY_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => togglePersonalityType(type.id)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${personalityTypes.includes(type.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${personalityTypes.includes(type.id) ? 'bg-primary text-white' : 'bg-muted'}`}>
                    <Icon name={personalityTypes.includes(type.id) ? 'Check' : type.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{type.name}</h4>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Your Vibe</h3>
          <select value={communicationStyle} onChange={(e) => setCommunicationStyle(e.target.value)} className="w-full md:w-64 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
            <option value="casual">Casual</option>
            <option value="warm">Warm</option>
            <option value="professional">Professional</option>
            <option value="humorous">Humorous</option>
          </select>
        </div>

        {userProfile?.recent_updates && (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">Latest life update</h3>
            <p className="text-sm text-foreground mb-1">&ldquo;{userProfile.recent_updates}&rdquo;</p>
            <p className="text-xs text-muted-foreground">
              {userProfile.recent_updates_at
                ? `Saved ${new Date(userProfile.recent_updates_at).toLocaleDateString()} — used in messages for about 60 days`
                : 'Used in messages for about 60 days'}
            </p>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">First-Touch Introduction</h3>
          <p className="text-sm text-muted-foreground mb-3">
            This is the <strong>first SMS</strong> a new contact gets. When they become due for their
            first evergreen touchpoint, this intro is sent <strong>instead of</strong> that month&apos;s
            monthly message (it uses that send slot); normal monthly touchpoints resume afterward.
            {/* Review Ask temporarily hidden:
            If Review Ask auto is on, the review ask comes at least 7 days after this first touch —
            not before. */}
            {' '}Use{' '}
            <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{first_name}'}</code> and{' '}
            <code className="bg-muted px-1 py-0.5 rounded text-xs">{'{agent_name}'}</code> as placeholders.
          </p>
          <textarea
            className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
            value={introTemplate}
            onChange={(e) => { setIntroTemplate(e.target.value); setIntroIsCustom(true); }}
            placeholder={DEFAULT_INTRO_TEMPLATE}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {introIsCustom ? 'Custom message active' : 'Using default message'}
            </p>
            {introIsCustom && (
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => { setIntroTemplate(DEFAULT_INTRO_TEMPLATE); setIntroIsCustom(false); }}
              >
                Reset to default
              </button>
            )}
          </div>
          <div className="mt-4">
            <TouchpointImageUploader
              imageUrl={firstContactImageUrl}
              purpose="first-touch"
              alignHint="your first-touch intro"
              onSaved={async (url) => {
                setFirstContactImageUrl(url);
                if (reloadProfile) {
                  try {
                    await reloadProfile();
                  } catch {
                    // non-blocking
                  }
                }
              }}
            />
          </div>
        </div>
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button onClick={handleSave} loading={saving} className="w-full md:w-auto">
            <Icon name="Save" size={16} className="mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalProfileSettings;
